import { isOpenInApp } from "@repo/shared-utils/pwa-utils";
import { APP_PROMPTS } from "@repo/shared-utils/pwa.constants";

console.log("Fireworks script loaded");

const handleInAppRedirection = (appUrl: string): void => {
  if (!appUrl?.includes("bajajfinserv.in") && appUrl?.startsWith("https:")) {
    window.prompt(`openInAppWebViewActivity://{"url":"${appUrl}","title":"Bajaj Finserv"}`);
  } else if (appUrl?.includes("bajajsuperapp") && !appUrl?.startsWith("https:")) {
    const timestamp = new Date()
      .toISOString()
      .replace(/[-:T.]/g, "")
      .slice(0, 14);
    let appUrlWithTimestamp = appUrl;
    if (appUrl.includes("SSO")) {
      if (appUrl.includes("?")) {
        appUrlWithTimestamp = `${appUrl}&ts=${timestamp}`;
      } else {
        appUrlWithTimestamp = `${appUrl}?ts=${timestamp}`;
      }
    }
    const windowWithTarsier = window as Window & {
      tarsier?: { invokePrompt: (prompt: string, force?: boolean) => unknown };
    };
    windowWithTarsier.tarsier?.invokePrompt(
      `${APP_PROMPTS.IN_APP_REDIRECTION}${appUrlWithTimestamp}`
    );
  } else {
    window.location.assign(appUrl);
  }
};

export const handleBothAppWebRedirection = (unitUrl: string): void => {
  const [webUrl, appUrl] = unitUrl.split("@");
  if (isOpenInApp()) {
    if (appUrl) {
      handleInAppRedirection(appUrl);
      console.log("app only", appUrl);
    }
  } else if (webUrl) {
    const target = "_self";
    window.open(webUrl, target);
    console.log(webUrl, "web only");
  }
};

export const handleRedirection = (unitUrl: string): void => {
  if (isOpenInApp()) {
    handleInAppRedirection(unitUrl);
    console.log(unitUrl, "unit url without @ for app");
  } else {
    const target = "_self";
    window.open(unitUrl, target);
    console.log(unitUrl, "unit url without @ for web");
  }
};

/**
 * Handles the click event for the primary CTA in the Firework embed.
 * It extracts product details and redirects to the appropriate URL based on the app/web context.
 * It also logs the event to the data layer for analytics.
 *
 * @param {Event} event - The click event triggered by the Firework embed.
 */
export const handlePrimaryCtaClick = (event: Event): void => {
  const customEvent = event as CustomEvent;
  const unitUrl = customEvent?.detail?.product?.product_units[0]?.unit_url || "";

  if (unitUrl) {
    console.log("unitUrl", unitUrl);
    if (unitUrl?.includes("@")) {
      handleBothAppWebRedirection(unitUrl);
    } else {
      handleRedirection(unitUrl);
    }
  }
};

const AVATAR_ALT_PREFIX = "Avatar of";
const AVATAR_CURSOR_STYLE_ID = "bfl-fw-avatar-cursor";
const AVATAR_DIRECT_CHILD_SELECTOR = ":scope > img[alt^='Avatar of']";
const AVATAR_IMAGE_SELECTOR = "img[alt^='Avatar of']";
const AVATAR_CURSOR_CSS = [
  'button[data-testid="video-controls-more"],',
  'button[data-testid="video-controls-more"] *,',
  "button.cy-more,",
  "button.cy-more *,",
  'img[alt^="Avatar of"] { cursor: default !important; }',
].join("\n");

type AttachShadow = typeof Element.prototype.attachShadow;

let nativeAttachShadow: AttachShadow | undefined;

const isAvatarImage = (node: Element): boolean =>
  node instanceof HTMLImageElement &&
  (node.getAttribute("alt") ?? "").startsWith(AVATAR_ALT_PREFIX);

const hasDirectAvatarChild = (node: Element): boolean =>
  Boolean(node.querySelector(AVATAR_DIRECT_CHILD_SELECTOR));

const isButtonContainingAvatar = (node: Element): boolean =>
  node instanceof HTMLButtonElement && Boolean(node.querySelector(AVATAR_IMAGE_SELECTOR));

/**
 * Firework channel avatar (`img[alt^="Avatar of"]`), a wrapper whose direct child
 * is that image, and a button that contains it are clickable inside open shadow DOM.
 * Block those clicks so they do not navigate away.
 */
const isFireworkChannelAvatarTarget = (event: Event): boolean =>
  event
    .composedPath()
    .some(
      (node) =>
        node instanceof Element &&
        (isAvatarImage(node) || hasDirectAvatarChild(node) || isButtonContainingAvatar(node))
    );

const appendAvatarCursorStyle = (root: ShadowRoot): void => {
  if (root.querySelector(`#${AVATAR_CURSOR_STYLE_ID}`)) return;

  const style = document.createElement("style");
  style.id = AVATAR_CURSOR_STYLE_ID;
  style.textContent = AVATAR_CURSOR_CSS;
  root.append(style);
};

const openShadowRootFromEvent = (event: Event): ShadowRoot | null => {
  for (const node of event.composedPath()) {
    if (node instanceof ShadowRoot && node.mode === "open") return node;
  }
  return null;
};

/**
 * Patches attachShadow so every open shadow root gets the default cursor
 * stylesheet immediately, before the avatar image exists.
 */
export const installAvatarCursorShadowPatch = (): void => {
  if (nativeAttachShadow) return;

  nativeAttachShadow = Element.prototype.attachShadow;
  const originalAttachShadow = nativeAttachShadow;

  Element.prototype.attachShadow = function patchedAttachShadow(init: ShadowRootInit): ShadowRoot {
    const shadowRoot = originalAttachShadow.call(this, init);
    if (init.mode === "open") {
      appendAvatarCursorStyle(shadowRoot);
    }
    return shadowRoot;
  };
};

/** Restores the native Element.attachShadow implementation. */
export const uninstallAvatarCursorShadowPatch = (): void => {
  if (!nativeAttachShadow) return;
  Element.prototype.attachShadow = nativeAttachShadow;
  nativeAttachShadow = undefined;
};

export const handleChannelAvatarClick = (event: Event): void => {
  if (!isFireworkChannelAvatarTarget(event)) return;
  event.preventDefault();
  event.stopPropagation();
};

/** Pointerover fallback. Does not add a second stylesheet when one is already present. */
export const handleChannelAvatarCursor = (event: Event): void => {
  const root = openShadowRootFromEvent(event);
  if (!root) return;
  appendAvatarCursorStyle(root);
};

/**
 * Initialize Fireworks event listeners.
 * This function should be called after the Firework script is loaded.
 */
export const initFireworks = (): void => {
  document.removeEventListener("fw:shopping:product-clicked", handlePrimaryCtaClick);
  document.removeEventListener("click", handleChannelAvatarClick, true);
  document.removeEventListener("pointerover", handleChannelAvatarCursor, true);

  document.addEventListener("fw:shopping:product-clicked", handlePrimaryCtaClick);
  document.addEventListener("click", handleChannelAvatarClick, true);
  document.addEventListener("pointerover", handleChannelAvatarCursor, true);

  console.log("Fireworks event listeners initialized");
};

/**
 * Cleanup Fireworks event listeners.
 * This function should be called when component unmounts or when cleanup is needed.
 */
export const cleanupFireworks = (): void => {
  document.removeEventListener("fw:shopping:product-clicked", handlePrimaryCtaClick);
  document.removeEventListener("click", handleChannelAvatarClick, true);
  document.removeEventListener("pointerover", handleChannelAvatarCursor, true);
  uninstallAvatarCursorShadowPatch();
  console.log("Fireworks event listeners cleaned up");
};
